import { selectIsPublishToEventsDirty } from '@app/routes/content/selectors';
import {
  isFree,
  isFreeTrial,
} from '@app/routes/organization/routes/manage/routes/billing/utils';
import { selectOrganizationPaymentPlanName } from '@app/routes/organization/selectors';
import { getPathParams } from '@app/routes/utils';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useCallback, useRef, useState } from 'react';

import { useRecordingAnalytics } from '../../hooks';
import type { ShareActionsHandle } from './share-actions';
import type { TStep } from './types';
import { STEP } from './types';

/**
 * Owns the share menu mini-flow:
 * - open/closed state
 * - current step (share actions vs publish-to-events)
 * - the dirty-exit discard confirmation
 */
export const useShareMenuFlow = () => {
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { recordingId } = getPathParams(['recordingId']);
  const isDirty = useAppSelector(selectIsPublishToEventsDirty);
  const paymentPlanName = useAppSelector(selectOrganizationPaymentPlanName);
  const canUsePublishToEvents =
    Boolean(paymentPlanName) &&
    !isFree(paymentPlanName) &&
    !isFreeTrial(paymentPlanName);

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<TStep>(STEP.SHARE_ACTIONS);
  const [isConfirmationShown, setIsConfirmationShown] = useState(false);
  const [actionAfterDiscard, setActionAfterDiscard] = useState<
    'back' | 'close'
  >('close');

  const publishTriggerRef = useRef<ShareActionsHandle>(null);

  const shouldConfirmExit = step === STEP.PUBLISH_TO_EVENT && isDirty;

  const close = useCallback(() => {
    setIsOpen(false);
    setStep(STEP.SHARE_ACTIONS);
    dispatch({ type: '[content] publish-to-events state reset' });
  }, [dispatch]);

  const open = useCallback(() => {
    setIsOpen(true);

    if (recordingId && canUsePublishToEvents) {
      dispatch({
        type: '[content] publish-to-events assignments requested',
        payload: { recordingId },
      });
    }
  }, [canUsePublishToEvents, dispatch, recordingId]);

  const goBack = useCallback(() => {
    dispatch({ type: '[content] publish-to-events back action clicked' });
    setStep(STEP.SHARE_ACTIONS);

    requestAnimationFrame(() => {
      publishTriggerRef.current?.focusPublishTrigger();
    });
  }, [dispatch]);

  const requestClose = useCallback(() => {
    if (isConfirmationShown) return;

    if (shouldConfirmExit) {
      setActionAfterDiscard('close');
      setIsConfirmationShown(true);
      return;
    }

    close();
  }, [close, isConfirmationShown, shouldConfirmExit]);

  const requestBack = useCallback(() => {
    if (shouldConfirmExit) {
      setActionAfterDiscard('back');
      setIsConfirmationShown(true);
      return;
    }

    goBack();
  }, [goBack, shouldConfirmExit]);

  const toggleMenu = useCallback(() => {
    trackRecordingEvent('ch_recording_editor_share_clicked');

    if (isOpen) {
      requestClose();
      return;
    }

    open();
  }, [isOpen, open, requestClose, trackRecordingEvent]);

  const goToPublishStep = useCallback(() => {
    dispatch({ type: '[content] publish-to-events list requested' });
    setStep(STEP.PUBLISH_TO_EVENT);
  }, [dispatch]);

  const keepEditing = useCallback(() => {
    setIsConfirmationShown(false);
  }, []);

  const discardChanges = useCallback(() => {
    setIsConfirmationShown(false);

    if (actionAfterDiscard === 'back') {
      goBack();
      return;
    }

    close();
  }, [actionAfterDiscard, close, goBack]);

  return {
    isOpen,
    step,
    publishTriggerRef,
    isConfirmationShown,
    toggleMenu,
    goToPublishStep,
    requestBack,
    requestClose,
    keepEditing,
    discardChanges,
  };
};
